import { getUUID } from '@bit/gerenciatd.utils.monitoring';
import {
  isRedirectToLoginError,
  makeLoginRedirect,
  makeUndefinedValueNull,
  getTelcoHost,
  USER_TYPES,
  generalAdabyronBehaviour,
} from '@bit/gerenciatd.utils.ada-byron';
import {
  MethodType,
  UnknownObject,
  WSElementType,
} from '@bit/gerenciatd.utils.types';
import Sonda from '@bit/gerenciatd.utils.sonda';
import {
  normalizeCreateCQResponse,
  normalizeGetProvisionalAgreementResponse,
} from './normalizers';

export enum PATHS {
  CREATE_CQ = 'ventas/inicializar/pedido',
  CANCEL_ALL = 'ventas/finalizar/cancelar/todas',
  SELECT_ELEMENTS = 'ventas/configurar/{cqId}/elegir/elementos',
  GET_FEATURES = 'ventas/configurar/{cqId}/recuperar/caracteristicas',
  ADD_TO_CART = 'ventas/configurar/{cqId}/agregar/alcarro',
  EXTEND_CQ = 'ventas/configurar/{cqId}/ampliar',
  CANCEL_CQ = 'ventas/finalizar/{cqId}/cancelar',
  GENERATE_PIN = 'ventas/pin/generar',
  CHECK_PIN = 'ventas/pin/comprobar',
  GET_POTS = 'ventas/configurar/{cqId}/recuperar/medios',
  UPDATE_CQ = 'ventas/configurar/{cqId}/actualizar/info',
  GET_CQ_PRICES = 'ventas/configurar/{cqId}/recuperar/precios',
  GET_LEGAL_TERMS = 'ventas/finalizar/{cqId}/recuperar/legales',
  CONFIRM_CQ = 'ventas/finalizar/{cqId}/confirmar',
  GET_PROVISIONAL_AGREEMENT = 'ventas/finalizar/{cqId}/recuperar/precontrato',
  GET_CUSTOMER_PRODUCTS_TO_HIRE_PRODUCT_OFFERING = 'ventas/buscar/producto',
  GET_SELLING_TYPES_BY_PRODUCT_ID = 'ventas/buscar/acciones',
}

export const createCQ = (
  data: {
    productOffering: string;
    productId?: string;
    promotion?: string;
    forzarErrorMigra?: string;
    subType?: string;
    parentProductId?: string;
  },
  eventName = 'CQV2.INIT_CQ',
  context: unknown,
  idFront = 'VT',
): Promise<unknown> => {
  const {
    productOffering,
    productId,
    promotion,
    forzarErrorMigra,
    subType,
    parentProductId,
  } = data;

  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.CREATE_CQ,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName,
    queryParams: [
      { param: 'productOfferingDestino', value: productOffering },
      { param: 'productId', value: productId },
      { param: 'promocionDestino', value: promotion },
      { param: 'forzarErrorMigra', value: forzarErrorMigra },
      { param: 'subType', value: subType },
      { param: 'productIdPadre', value: parentProductId },
    ],
    mockedInfo: 'createCQ',
    methodType: MethodType.GET,
    onSuccessCallback: (xhr: XMLHttpRequest): UnknownObject => {
      const { response, responseURL } = xhr;

      if (isRedirectToLoginError(responseURL)) {
        makeLoginRedirect(responseURL);
        return null;
      }
      const { datos } = JSON.parse(response);
      return normalizeCreateCQResponse(datos, context);
    },
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const cancelAll = (
  context: unknown,
  idFront = 'VT',
): Promise<unknown> => {
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.CANCEL_ALL,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.CANCEL_ALL',
    mockedInfo: 'cancelAll',
    methodType: MethodType.POST,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const initCQ = (
  data: {
    productOffering: string;
    productId?: string;
    promotion?: string;
    forzarErrorMigra?: string;
    subType?: string;
    parentProductId?: string;
  },
  context: any,
  idFront = 'VT',
): Promise<any> => {
  return new Promise((resolve, reject) => {
    const idRequest = getUUID(idFront);
    const { processName } = getTelcoHost(context, '', [
      USER_TYPES.CLIENT,
      USER_TYPES.TGES,
      USER_TYPES.OPEN,
    ]);

    const sonda = new Sonda({
      eventName: 'CQV2.GLOBAL_INIT_CQ',
      context,
      uniqueId: idRequest,
      origin: processName,
    });

    try {
      sonda.startWS();
      createCQ(data, 'CQV2.INIT_CQ', context, idFront)
        .then((createCQResponse) => {
          sonda.endWS(WSElementType.OK);
          resolve(createCQResponse);
        })
        .catch((createCQErr) => {
          const { status, response } = createCQErr;
          // CONTINGENCIA: Si hay alguna CQ en vuelo las cancelamos y volvemos a empezar
          if (
            status === 412 &&
            response &&
            response.error &&
            response.error.codigoErrorNegocio === 'TCOL-VENTAS-008-2'
          ) {
            cancelAll(context, idFront)
              .then(() => {
                createCQ(data, 'CQV2.INIT_CQ_RELAUNCH', context, idFront)
                  .then((initCQRelaunchResponse) => {
                    sonda.endWS(WSElementType.OK);
                    resolve(initCQRelaunchResponse);
                  })
                  .catch((initCQRelaunchErr) => {
                    sonda.endWS(WSElementType.ERROR);
                    reject(initCQRelaunchErr);
                  });
              })
              .catch((cancelAllErr) => {
                sonda.endWS(WSElementType.ERROR);
                reject(cancelAllErr);
              });
          } else {
            sonda.endWS(WSElementType.ERROR);
            reject(createCQErr);
          }
        });
    } catch (error) {
      sonda.errorJs(error);
    }
  })
    .then((initCQRes) => {
      return initCQRes;
    })
    .catch((initCQErr) => {
      throw initCQErr;
    });
};

export const selectElements = (
  data: {
    cqId: string;
    chosenActionsArray?: Array<{
      id: string;
      customerQuoteItems: Array<{
        cqsiId: string;
        clientPhoneNumberToBeAppliedTo: string;
      }>;
    }>;
    cqsiId?: string;
    clientPhoneNumberToBeAppliedTo?: string;
  },
  context: unknown,
  idFront = 'VT',
): Promise<unknown> => {
  const {
    cqId,
    chosenActionsArray = [],
    cqsiId,
    clientPhoneNumberToBeAppliedTo,
  } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.SELECT_ELEMENTS,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.SELECT_ELEMENTS',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'selectElements',
    methodType: MethodType.POST,
    requestObject: makeUndefinedValueNull({
      chosenActions: chosenActionsArray,
      cqsiId,
      clientPhoneNumberToBeAppliedTo,
    }),
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const getFeatures = (
  data: {
    cqId: string;
    chosenActionsArray?: {
      id: string;
      customerQuoteItems: {
        id: string;
      }[];
    }[];
  },
  context: unknown,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId, chosenActionsArray = [] } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_FEATURES,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.GET_FEATURES',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'getFeatures',
    methodType: MethodType.POST,
    requestObject: makeUndefinedValueNull({
      chosenActions: chosenActionsArray,
    }),
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const addToCart = (
  data: {
    cqId: string;
    chosenActionsArray?: {
      id: string;
      clientPhoneNumberToBeAppliedTo: string;
      offeringCharacteristics: {
        id: string;
        value: string;
      }[];
    }[];
    showPrices?: boolean;
  },
  context: unknown,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId, chosenActionsArray = [], showPrices = false } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.ADD_TO_CART,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.ADD_TO_CART',
    queryParams: [{ param: 'mostrarPrecios', value: showPrices }],
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'addToCart',
    methodType: MethodType.POST,
    requestObject: makeUndefinedValueNull({
      chosenActions: chosenActionsArray,
    }),
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const extendCQ = (
  data: {
    cqId: string;
    productOffering: string;
    promotion?: string;
  },
  context: unknown,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId, productOffering, promotion } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.EXTEND_CQ,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.EXTEND_CQ',
    queryParams: [
      {
        param: 'productOfferingId',
        value: productOffering,
      },
      { param: 'productPromotionId', value: promotion },
    ],
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'extendCQ',
    methodType: MethodType.GET,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const cancelCQ = (
  data: {
    cqId: string;
  },
  context: unknown,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.CANCEL_CQ,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.CANCEL_CQ',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'cancelCQ',
    methodType: MethodType.POST,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const generatePIN = (
  data: {
    clientPhoneNumber: string;
  },
  context: unknown,
  idFront = 'VT',
): Promise<unknown> => {
  const { clientPhoneNumber } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GENERATE_PIN,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'CQV2.GENERATE_PIN',
    queryParams: [{ param: 'numTelefono', value: clientPhoneNumber }],
    mockedInfo: 'generatePIN',
    methodType: MethodType.GET,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const checkPIN = (
  data: {
    clientPhoneNumber: string;
    pinToCheck: string;
    pinRequestId: string;
  },
  context: unknown,
  idFront = 'VT',
): Promise<unknown> => {
  const { clientPhoneNumber, pinToCheck, pinRequestId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.CHECK_PIN,
    userTypes: [USER_TYPES.CLIENT],
    eventName: 'CQV2.CHECK_PIN',
    queryParams: [{ param: 'numTelefono', value: clientPhoneNumber }],
    mockedInfo: 'checkPIN',
    methodType: MethodType.POST,
    requestObject: makeUndefinedValueNull({
      clientPhoneNumberToBeAppliedTo: clientPhoneNumber,
      pin: pinToCheck,
      pinRequestId,
    }),
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const getPOTS = (
  data: {
    cqId: string;
  },
  context: unknown,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_POTS,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.GET_POTS',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'getPOTS',
    methodType: MethodType.GET,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const updateCQ = (
  data: {
    cqId: string;
    locationId?: string;
    sms: string;
    phone: string;
    choices?: Array<{
      id: string;
      type: string;
      customerAccount: {
        bankEntityCode: string;
        bankOfficeCode: string;
        customerAccountId: string;
        customerAccountName: string;
        ibanCheckDigits: string;
        ibanCountryIso2Code: string;
        locationId: string;
        spanishIbanAccountNumber: string;
        spanishIbanCheckDigits: string;
      };
    }>;
    riskEvaluation?: Array<{
      chosenId: string;
      id: string;
      value: string;
    }>;
  },
  context: unknown,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId, locationId, sms, phone, choices = [], riskEvaluation } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.UPDATE_CQ,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.UPDATE_CQ_V2',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'updateCQ',
    methodType: MethodType.POST,
    requestObject: makeUndefinedValueNull({
      choices,
      contactMedias: {
        locationId,
        sms,
        telf: phone,
      },
      riskEvaluation,
    }),
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const getCQPrices = (
  data: {
    cqId: string;
    chosenActions?: Array<{
      id: string;
      pinRequestId: string;
      clientPhoneNumberToBeAppliedTo: string;
      pin: string;
      actionCharacteristics: Array<{
        id: string;
        value: string;
        name: string;
      }>;
    }>;
  },
  context: unknown,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId, chosenActions = [] } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_CQ_PRICES,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.GET_CQ_PRICES',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'getCQPrices',
    methodType: MethodType.POST,
    requestObject: makeUndefinedValueNull({
      chosenActions,
    }),
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const getLegalTerms = (
  data: {
    cqId: string;
  },
  context: unknown,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_LEGAL_TERMS,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.GET_LEGAL_TERMS',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'getLegalTerms',
    methodType: MethodType.GET,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const confirmCQ = (
  data: {
    cqId: string;
  },
  context: unknown,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.CONFIRM_CQ,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.CONFIRM_CQ',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'confirmCQ',
    methodType: MethodType.POST,
    onSuccessCallback: (xhr: XMLHttpRequest): string => {
      const { response, responseURL } = xhr;
      if (isRedirectToLoginError(responseURL)) {
        makeLoginRedirect(responseURL);
        return null;
      }
      const { datos } = JSON.parse(response);
      return datos.customerOrder.code;
    },
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const getProvisionalAgreement = (
  data: {
    cqId: string;
  },
  context: unknown,
  idFront = 'VT',
): Promise<unknown> => {
  const { cqId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_PROVISIONAL_AGREEMENT,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.GET_PROVISIONAL_AGREEMENT',
    urlParamsToReplace: [{ param: '{cqId}', value: cqId }],
    mockedInfo: 'getProvisionalAgreement',
    methodType: MethodType.GET,
    onSuccessNormalizer: normalizeGetProvisionalAgreementResponse,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const getCustomerProductsToHireProductOffering = (
  data: {
    productOffering: string;
  },
  context: unknown,
  idFront = 'VT',
): Promise<unknown> => {
  const { productOffering } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_CUSTOMER_PRODUCTS_TO_HIRE_PRODUCT_OFFERING,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.GET_CUSTOMER_PRODUCTS_TO_HIRE_PRODUCT_OFFERING',
    queryParams: [{ param: 'id', value: productOffering }],
    mockedInfo: 'getCustomerProductsToHireProductOffering',
    methodType: MethodType.GET,
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const getSellingTypesByProductId = (
  data: { productId: string },
  context: unknown,
  idFront = 'VT',
): Promise<unknown> => {
  const { productId } = data;
  return generalAdabyronBehaviour({
    context,
    idFront,
    path: PATHS.GET_SELLING_TYPES_BY_PRODUCT_ID,
    userTypes: [USER_TYPES.CLIENT, USER_TYPES.TGES, USER_TYPES.OPEN],
    eventName: 'CQV2.GET_SELLING_TYPES_BY_PRODUCT_ID',
    queryParams: [{ param: 'productId', value: productId }],
    mockedInfo: 'getSellingTypesByProductId',
    methodType: MethodType.GET,
    onSuccessCallback: (xhr: XMLHttpRequest) => {
      const { response, responseURL } = xhr;
      if (isRedirectToLoginError(responseURL)) {
        makeLoginRedirect(responseURL);
      } else {
        const { datos } = JSON.parse(response);
        return { productId, ...datos };
      }
    },
    requestHeaders: { 'Content-Type': 'application/json;charset=UTF-8' },
  });
};

export const filterCustomerProductToHireWithActions = (
  data: {
    productOffering: string;
    productTypesTCOL?: string[];
  },
  context: any,
  idFront = 'VT',
): Promise<unknown> => {
  return new Promise((resolve, reject) => {
    const idRequest = getUUID(idFront);
    const { productOffering, productTypesTCOL } = data;
    const { processName } = getTelcoHost(context, '', [
      USER_TYPES.CLIENT,
      USER_TYPES.TGES,
      USER_TYPES.OPEN,
    ]);
    const sonda = new Sonda({
      eventName: 'CQV2.FILTER_CUSTOMER_PRODUCT_TO_HIRE_WITH_ACTIONS',
      context,
      uniqueId: idRequest,
      origin: processName,
    });
    try {
      sonda.startWS();
      getCustomerProductsToHireProductOffering(
        { productOffering },
        context,
        idFront,
      )
        .then((res: any) => {
          let filteredInfo = [];
          let { products } = res;

          if (Array.isArray(productTypesTCOL) && productTypesTCOL.length > 0) {
            products = products.filter((product) =>
              productTypesTCOL.includes(product.productTypeTCOL),
            );
          }
          const promiseArray = [];

          products.forEach((product) => {
            product.choices.forEach((choice) => {
              const { id: productId } = choice;

              promiseArray.push(
                getSellingTypesByProductId({ productId }, context, idFront),
              );
            });
          });

          Promise.all(promiseArray)
            .then((results) => {
              filteredInfo = products.map((product) => {
                const { productTypeTCOL, choices } = product;
                const filteredChoices = choices.filter((choice) => {
                  return results.some(
                    // eslint-disable-next-line max-len
                    (result) =>
                      result.productId === choice.id &&
                      Array.isArray(result.choices) &&
                      result.choices.length > 0,
                  );
                });

                return {
                  productTypeTCOL,
                  choices: filteredChoices.map((choice) => {
                    const actionByProductId = results.find(
                      (result) => result.productId === choice.id,
                    );

                    return {
                      ...choice,
                      subTypeChoices: actionByProductId.choices,
                    };
                  }),
                };
              });
              sonda.endWS(WSElementType.OK);
              resolve(filteredInfo);
            })
            .catch((err) => {
              reject(err);
              sonda.endWS(WSElementType.ERROR);
            });
        })
        .catch((err) => {
          sonda.endWS(WSElementType.ERROR);
          reject(err);
        });
    } catch (error) {
      sonda.errorJs(error.toString());
    }
  })
    .then((res) => {
      return res;
    })
    .catch((err) => {
      throw err;
    });
};
